import { Section } from 'react-scale/components';
import clsx from 'clsx';
import { CountdownProvider, useCountdownStore } from '@bsmnt/drop';
import * as React from 'react';
import { DrawSVGPlugin } from 'gsap/dist/DrawSVGPlugin';

import dynamic from 'next/dynamic';
import { gsap } from 'react-scale/lib/gsap';
import s from './counter.module.scss';

const Spline = dynamic(() => import('@splinetool/react-spline'), { ssr: false });

type CounterProps = {
  startDate?: string;
  endDate: string;
  title: string;
};

export const Counter = ({ startDate = '2022-11-01T09:00Z', endDate, title }: CounterProps) => {
  const [hasRendered, setHasRendered] = React.useState(false);

  React.useEffect(() => {
    setHasRendered(true);
  }, []);

  return (
    <Section>
      <div className="relative text-black lg:h-[200px] flex-col lg:flex-row items-center overflow-hidden flex gap-y-8 justify-between lg:px-20 py-12 border border-[#B7B7B7] rounded-2xl bg-[linear-gradient(180deg,_#C7C7C7_3.49%,_#FFFFFF_62.53%)]">
        <h2 className={clsx(s.title, 'font-forest text-3xl lg:text-[34px] leading-[1.35] z-10')}>
          {title}
        </h2>
        <figure className="absolute h-full w-full left-0 top-0 hidden xl:block pointer-events-none">
          <Spline scene="https://prod.spline.design/5raoQBUXaafnGkiQ/scene.splinecode" />
        </figure>

        <CountdownProvider startDate={new Date(startDate)} endDate={new Date(endDate)}>
          {hasRendered && <Countdown />}
        </CountdownProvider>
      </div>
    </Section>
  );
};

const Countdown = () => {
  const humanTimeRemaining = useCountdownStore()(
    state => state.humanTimeRemaining // keep in mind this is zustand, so you can slice this store
  );

  return (
    <div className="text-[#494949] z-10 text-[58px] lg:flex gap-y-7 gap-x-4 lg:gap-x-[43px] grid grid-cols-2">
      <CircularProgress
        timeNumber={Number(humanTimeRemaining.days)}
        timeUnit="days"
        maxTime={200}
      />
      <CircularProgress
        timeNumber={Number(humanTimeRemaining.hours)}
        timeUnit="hours"
        maxTime={24}
      />
      <CircularProgress timeNumber={Number(humanTimeRemaining.minutes)} timeUnit="mins" />
      <CircularProgress timeNumber={Number(humanTimeRemaining.seconds)} timeUnit="secs" />
    </div>
  );
};

const CircularProgress = ({
  maxTime = 59,
  ...props
}: {
  timeNumber: number;
  timeUnit: string;
  maxTime?: number;
}) => {
  const progressRef = React.useRef(null);
  const numbRef = React.useRef(null);

  // FIXME: On not-second times it stays empty
  React.useEffect(() => {
    const drawSVG = (DrawSVGPlugin.getLength(progressRef.current) / maxTime) * props.timeNumber;
    const tl = gsap.timeline();
    tl.to(progressRef.current, { drawSVG });
    if (props.timeNumber <= 0 && props.timeUnit === 'secs') {
      tl.to(numbRef.current, { y: 150, duration: 0.4 }, 0.4);
    }

    if (props.timeNumber >= maxTime) {
      tl.set(numbRef.current, { y: -150 }, 0);
      tl.to(numbRef.current, { y: 0, duration: 0.4 }, '<0.1');
    }

    return () => {
      tl.kill();
    };
  }, [props.timeNumber]);

  return (
    <div className="relative h-[128px] w-[128px] flex items-center justify-center overflow-y-hidden">
      <svg
        viewBox="0 0 128 128"
        width="128"
        height="128"
        className="z-10 -rotate-90 absolute -top-[0px] -left-[0px]"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle strokeLinecap="round" cx="64" cy="64" r="60" stroke="#B5B5B5" strokeWidth={1} />
        <circle
          strokeLinecap="round"
          ref={progressRef}
          strokeDashoffset={0}
          strokeDasharray={0}
          cx="64"
          cy="64"
          r="60"
          stroke="url(#grayscale-countdown-gradient)"
          strokeWidth={6}
        />
        <defs>
          <linearGradient id="grayscale-countdown-gradient">
            <stop offset="0%" stopColor="#D9D9D9" />
            <stop offset="95%" stopColor="#000000" />
          </linearGradient>
        </defs>
      </svg>
      <div className="flex overflow-hidden">
        <span ref={numbRef} className="leading-[1.1]">
          {props.timeNumber}
        </span>
      </div>
      <span className="absolute bottom-4 -translate-x-1/2 left-1/2 text-xs uppercase">
        {props.timeUnit}
      </span>
    </div>
  );
};
